import { fabric } from 'fabric';

import { ToolMode } from './mode';
import { SerializedObject } from './types';
import { flattenObjects, isMap, normalizeObjectUUID, uuidv4 } from './util';

export interface InternalCanvasInit extends fabric.ICanvasOptions {
    toolMode: ToolMode;
    initialObjects?: SerializedObject[];
    generateUUID?: () => string;
}

export class InternalCanvas {
    public _fabricCanvas: fabric.Canvas;
    private _generateUUID: () => string;
    private _objectMutator: (o: fabric.Object) => void;

    constructor(
        canvasElem: HTMLCanvasElement,
        {
            toolMode,
            initialObjects,
            generateUUID,
            ...options
        }: InternalCanvasInit,
    ) {
        this._fabricCanvas = new fabric.Canvas(canvasElem, options);
        this._generateUUID = generateUUID || uuidv4;

        this._objectMutator = toolMode.objectMutator;
        toolMode.apply(this._fabricCanvas);

        if (initialObjects) {
            this.enlivenObjects(initialObjects)
                .then((objs) => this.addObjectsSilently(objs))
                .then(() => this.requestRenderAll());
        }
    }

    public set toolMode(mode: ToolMode) {
        this._objectMutator = mode.objectMutator;
        mode.apply(this._fabricCanvas);
    }

    public get zoom(): number {
        return this._fabricCanvas.getZoom();
    }

    public get width(): number {
        return this._fabricCanvas.getWidth();
    }

    public get height(): number {
        return this._fabricCanvas.getHeight();
    }

    public get brushWidth(): number {
        return this._fabricCanvas.freeDrawingBrush.width;
    }

    public set brushWidth(width: number) {
        this._fabricCanvas.freeDrawingBrush.width = width;
    }

    public get brushColor(): string {
        return this._fabricCanvas.freeDrawingBrush.color;
    }

    public set brushColor(color: string) {
        this._fabricCanvas.freeDrawingBrush.color = color;
    }

    public get activeObject(): fabric.Object | undefined {
        return this._fabricCanvas.getActiveObject();
    }

    public discardActiveObject(): void {
        this._fabricCanvas.discardActiveObject();
    }

    public requestRenderAll(): void {
        this._fabricCanvas.requestRenderAll();
    }

    public addFabricEventHandler(
        type: string | string[],
        handler: (event: fabric.IEvent) => void,
    ): () => void {
        const types = Array.isArray(type) ? type : [type];
        types.forEach((type) => this._fabricCanvas.on(type, handler));
        return () =>
            types.forEach((type) => this._fabricCanvas.off(type, handler));
    }

    public findObjectIndexByUUID(uuid: string): number {
        return this._fabricCanvas._objects.findIndex((o) => o.uuid === uuid);
    }

    public getObjectsByUUIDs(
        uuids: string[],
    ): Array<fabric.Object | undefined> {
        return uuids.map(this.getObjectByUUID, this);
    }

    public getObjectByUUID(uuid: string): fabric.Object | undefined {
        const index = this.findObjectIndexByUUID(uuid);
        if (index === -1) {
            return;
        }

        return this._fabricCanvas._objects[index];
    }

    public removeObjects(...objs: fabric.Object[]): void {
        this._fabricCanvas.remove(...objs);
    }

    public removeObjectSilently(
        obj: string | fabric.Object | undefined,
        replacement?: fabric.Object,
    ): boolean {
        if (!obj) {
            return false;
        }

        const index = this.findObjectIndexByUUID(normalizeObjectUUID(obj));
        if (index === -1) {
            return false;
        }

        this._fabricCanvas._objects.splice(
            index,
            1,
            ...(replacement ? [replacement] : []),
        );

        return true;
    }

    public removeObjectsSilently(
        objs: fabric.Object | string | Array<fabric.Object | string>,
    ): void {
        for (const obj of Array.isArray(objs) ? objs : [objs]) {
            this.removeObjectSilently(obj);
        }
    }

    public addObjects(...objs: fabric.Object[]): void {
        this._fabricCanvas.add(...objs.map(this.adoptObject, this));
        for (const obj of objs.filter(isMap)) {
            obj.sendToBack();
        }
    }

    public _addObjectsSilently(objs: fabric.Object | fabric.Object[]): void {
        objs = Array.isArray(objs) ? objs : [objs];
        this._fabricCanvas._objects.push(...objs.map(this.adoptObject, this));

        for (const obj of objs.filter(isMap)) {
            obj.sendToBack();
        }
    }

    public async addObjectsSilently(
        objs: fabric.Object | fabric.Object[],
    ): Promise<void> {
        this._addObjectsSilently(await flattenObjects(objs));
    }

    public async replaceObjectsSilently(
        objs: fabric.Object | fabric.Object[],
        inPlace = false,
    ): Promise<void> {
        for (const obj of await flattenObjects(objs)) {
            this._objectMutator(obj);
            if (inPlace) {
                if (!this.removeObjectSilently(obj.uuid, obj)) {
                    this._addObjectsSilently(obj);
                }
            } else {
                this.removeObjectSilently(obj.uuid);
                this._addObjectsSilently(obj);
            }
        }
    }

    public async enlivenObject(ded: SerializedObject): Promise<fabric.Object> {
        return (await this.enlivenObjects([ded]))[0];
    }

    public async enlivenObjects(
        ded: SerializedObject[],
    ): Promise<fabric.Object[]> {
        return (
            await new Promise<fabric.Object[]>((resolve) => {
                const clones = JSON.parse(JSON.stringify(ded));
                for (const obj of clones) {
                    obj.fill = obj.fill ?? 'rgba(0,0,0,0)';
                }

                fabric.util.enlivenObjects(clones, resolve, 'fabric');
            })
        ).map(this.adoptObject, this);
    }

    public adoptObject(obj: fabric.Object): fabric.Object {
        obj.canvas = this._fabricCanvas;
        if (!obj.uuid) {
            obj.uuid = this._generateUUID();
        }

        this._objectMutator(obj);

        return obj;
    }
}
